import React, { useCallback, useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getActiveLearner, setActiveLearner } from "@/lib/learner";
import { Copy, Check, Trash2, LogOut, User, Users } from "lucide-react";

// "Choose who is learning" for a parent account, the same flow as Little Verses: the children are the profiles created in the
// HikmahVerses dashboard (Profiles), shown here under the same name; a child can also be linked with a code. Tap a name to learn as
// them. A child has their own start date, weekly choice and progress. "Me" is the account's own journey and is kept.
// Profiles are created only in the dashboard, never here.
export default function LearnerPicker({ onChange }) {
  const { user } = useAuth();
  const myName = (user && user.name) || "Me";   // the account's own chip carries its owner's name
  const [children, setChildren] = useState(null);
  const [active, setActive] = useState(getActiveLearner());
  const [shared, setShared] = useState(null);      // { id, code }
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/children");
      setChildren(data.children || []);
      const current = getActiveLearner();
      if (current && !(data.children || []).some((c) => c.id === current)) {   // removed on another device
        setActiveLearner("");
        setActive("");
        if (onChange) onChange("");
      }
    } catch {
      setChildren([]);
    }
  }, [onChange]);
  useEffect(() => { load(); }, [load]);

  const choose = (id, childName = "") => {
    if (id === active) return;
    setActiveLearner(id, childName);
    setActive(id);
    setShared(null);
    if (onChange) onChange(id, childName);
  };

  const guard = async (fn) => {
    setBusy(true);
    setErr("");
    try { await fn(); } catch (ex) { setErr(formatApiError(ex.response?.data?.detail) || ex.message); } finally { setBusy(false); }
  };

  const showCode = (child) => guard(async () => {
    const { data } = await client.get(`/children/${child.id}/link-code`);
    setShared({ id: child.id, code: data.link_code });
    setCopied(false);
  });

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(shared.code); setCopied(true); } catch { /* the code stays on screen to read out */ }
  };

  const removeChild = (child) => {
    if (!window.confirm(`Remove ${child.name}? Their profile is deleted from your account.`)) return;
    guard(async () => {
      await client.delete(`/children/${child.id}`);
      if (active === child.id) choose("");
      await load();
    });
  };

  const unlinkChild = (child) => {
    if (!window.confirm(`Stop following ${child.name}? Their profile stays with the parent who added them.`)) return;
    guard(async () => {
      await client.post(`/children/${child.id}/unlink`);
      if (active === child.id) choose("");
      await load();
    });
  };

  if (children === null) return null;
  const selected = children.find((c) => c.id === active);
  const chip = (isActive) => `inline-flex items-center gap-2 rounded-full px-4 py-2.5 min-h-[44px] font-bold text-sm transition ${
    isActive ? "bg-hv-navy text-white" : "bg-white text-hv-navy border-2 border-[#E5DFC8] hover:bg-hv-cream"}`;

  return (
    <section className="rounded-[2rem] bg-white shadow-sm p-5 sm:p-6" data-testid="learner-picker">
      <h2 className="hv-heading text-xl text-hv-navy">Choose who is learning</h2>
      <p className="text-hv-slate text-sm mt-1">Pick a child to follow their own stories and progress. Profiles and linking are managed in your HikmahVerses dashboard.</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => choose("")} aria-pressed={!active} data-testid="learner-me" className={chip(!active)}>
          <User size={15} strokeWidth={2.5} /> {myName}
        </button>
        {children.map((c) => (
          <button key={c.id} type="button" onClick={() => choose(c.id, c.name)} aria-pressed={active === c.id} data-testid={`learner-chip-${c.id}`} className={chip(active === c.id)}>
            <Users size={15} strokeWidth={2.5} /> {c.name}
            {c.my_role === "linked" && <span className={`text-[10px] uppercase tracking-wider ${active === c.id ? "text-white/80" : "text-hv-slate"}`}>linked</span>}
          </button>
        ))}
      </div>

      {selected && (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm" data-testid="learner-selected">
          <span className="text-hv-slate">
            Learning as <b className="text-hv-navy">{selected.name}</b> · {selected.completed} {selected.completed === 1 ? "story" : "stories"} finished
          </span>
          {selected.my_role === "primary" ? (
            <>
              <button type="button" onClick={() => showCode(selected)} disabled={busy} data-testid="learner-share" className="inline-flex items-center gap-1.5 font-bold text-hv-teal hover:underline">
                <Copy size={14} strokeWidth={2.5} /> Share code with another parent
              </button>
              <button type="button" onClick={() => removeChild(selected)} disabled={busy} data-testid="learner-remove" className="inline-flex items-center gap-1.5 font-bold text-hv-slate hover:underline">
                <Trash2 size={14} strokeWidth={2.5} /> Remove child
              </button>
            </>
          ) : (
            <button type="button" onClick={() => unlinkChild(selected)} disabled={busy} data-testid="learner-unlink" className="inline-flex items-center gap-1.5 font-bold text-hv-slate hover:underline">
              <LogOut size={14} strokeWidth={2.5} /> Unlink
            </button>
          )}
        </div>
      )}

      {shared && shared.id === active && (
        <div className="mt-3 inline-flex items-center gap-3 rounded-2xl bg-hv-cream px-4 py-3" data-testid="learner-code-card">
          <span className="text-xs text-hv-slate">Link code</span>
          <b className="tracking-[0.3em] text-hv-navy text-lg" data-testid="learner-code-value">{shared.code}</b>
          <button type="button" onClick={copyCode} aria-label="Copy the code" className="text-hv-teal">
            {copied ? <Check size={16} strokeWidth={2.5} /> : <Copy size={16} strokeWidth={2.5} />}
          </button>
        </div>
      )}

      {err && <p className="mt-3 text-sm font-bold text-hv-coral" role="alert" data-testid="learner-error">{err}</p>}
    </section>
  );
}
